import type { SerieLayerStateItem } from '@/api/generated/schemas';
import { LayerState, SerieLayer } from '@/api/generated/schemas';
import { toSerieLayerKey, type SerieLayerKey } from './serieLayers';

/**
 * Odvození akcí schvalovacího tlačítka vrstvy – zrcadlí backendový `LayerStateMachine`.
 * Rychlé vrstvy (Termíny, Operation) přeskakují `Prepared` a schvalují se z `Draft`; vrstvy s přípravou
 * (Product cost, Calculation) se schvalují z `Prepared`.
 */

/** Používá vrstva mezistav `Prepared`? (zrcadlí `SerieLayerMap.UsesPrepared`) */
export function usesPrepared(layer: SerieLayer): boolean {
  return layer === SerieLayer.ProductCostScheme || layer === SerieLayer.Calculation;
}

/** Lze vrstvu v tomto stavu schválit? Rychlá z `Draft`, s přípravou z `Prepared`. */
export function isApprovable(layer: SerieLayer, state: LayerState): boolean {
  return usesPrepared(layer) ? state === LayerState.Prepared : state === LayerState.Draft;
}

/** Stav, který se počítá jako „hotovo“ (zrcadlí `SerieLayerMap.IsApproved`). */
function isApproved(state: LayerState): boolean {
  return state === LayerState.Approved;
}

export interface LayerActions {
  /** `Draft → Prepared` (jen vrstvy s přípravou). */
  canPrepare: boolean;
  /** `Prepared → Draft` (jen vrstvy s přípravou). */
  canReturnToDraft: boolean;
  /** `→ Approved` – jen na první neschválené vrstvě (předchozí je Approved), viz `buildApproveLayerPlan`. */
  canApprove: boolean;
  /** `Approved → Draft` – na libovolné schválené vrstvě; vrstvu Termíny jen na seriálu, ne na termínu. */
  canUnapprove: boolean;
}

/** Předchozí vrstva pipeline je schválená (nebo jde o první vrstvu Termíny)? Zrcadlí backendový `ApproveOne`. */
function isPreviousApproved(ordered: SerieLayerStateItem[], current: SerieLayerStateItem): boolean {
  const index = ordered.findIndex((entry) => entry.layer === current.layer);
  if (index <= 0) return true;
  return isApproved(ordered[index - 1].state);
}

/**
 * Lze vrátit schválení vrstvy Termíny? Jen na seriálu a jen dokud navazující práce nezačala – Doprava je
 * pořád `Draft` a Product cost i Calculation jsou `Locked`. Zrcadlí backendový `Terms` guard (stavová část;
 * kontrolu „vybraná doprava“ dělá až backend – pak přijde srozumitelná hláška).
 */
function canRevertTerms(ordered: SerieLayerStateItem[], isSerieScope: boolean): boolean {
  if (!isSerieScope) return false;
  const stateOf = (layer: SerieLayer) => ordered.find((entry) => entry.layer === layer)?.state;
  return (
    stateOf(SerieLayer.OperationScheme) === LayerState.Draft &&
    stateOf(SerieLayer.ProductCostScheme) === LayerState.Locked &&
    stateOf(SerieLayer.Calculation) === LayerState.Locked
  );
}

/** Které akce nabídnout pro vrstvu v daném stavu. */
export function layerActions(
  layer: SerieLayer,
  state: LayerState,
  layers: SerieLayerStateItem[],
  isSerieScope: boolean
): LayerActions {
  const prepared = usesPrepared(layer);
  const ordered = [...layers].sort((a, b) => a.sortOrder - b.sortOrder);
  const current = ordered.find((entry) => entry.layer === layer);

  return {
    canPrepare: prepared && state === LayerState.Draft,
    canReturnToDraft: prepared && state === LayerState.Prepared,
    // Schvalovat lze jen první neschválenou vrstvu (schvalovatelný stav + předchozí vrstva Approved).
    canApprove:
      current !== undefined && isApprovable(layer, state) && isPreviousApproved(ordered, current),
    // Vracet lze na libovolné schválené vrstvě; vrstvu Termíny jen na seriálu a jen s nedotčenými navazujícími.
    canUnapprove:
      state === LayerState.Approved &&
      (layer !== SerieLayer.Terms || canRevertTerms(ordered, isSerieScope)),
  };
}

export interface ApproveLayerOption {
  /** Cílová (nejvyšší) vrstva řetězu schválení. */
  layer: SerieLayer;
  /** Klíč pro i18n (`serieDetail.layers.<key>`). */
  layerKey: SerieLayerKey;
  sortOrder: number;
}

export interface ApproveLayerPlan {
  /** Vrstva, kterou schválí hlavní akce. `null` = schvalovat nelze (tlačítko se nezobrazí). */
  primary: ApproveLayerOption | null;
  /** Kumulativní alternativy „až po X“ (bez `primary`), vzestupně podle pořadí v pipeline. */
  through: ApproveLayerOption[];
}

export interface BuildApproveLayerPlanInput {
  /** Vrstvy schvalované entity (termínu, nebo seriálu). */
  layers: SerieLayerStateItem[];
  /**
   * Vrstvy nadřazeného seriálu – zdroj alternativ. Vynech ve scope seriálu (nad ním už nic není),
   * pak se nabídne jen hlavní akce.
   */
  serieLayers?: SerieLayerStateItem[];
  /** Vrstva, na které uživatel stojí (aktivní záložka / panel). */
  currentLayer: SerieLayer;
}

function toOption(entry: SerieLayerStateItem): ApproveLayerOption {
  return {
    layer: entry.layer,
    layerKey: toSerieLayerKey(entry.layer),
    sortOrder: entry.sortOrder,
  };
}

/**
 * Sestaví plán schválení.
 *
 * Hlavní akce (`primary`) se nabídne jen na **první neschválené** vrstvě – ve schvalovatelném stavu
 * (rychlá `Draft` / s přípravou `Prepared`) a s předchozí vrstvou `Approved`.
 *
 * Řetěz alternativ „až po X“ je **souvislý**: jde vzhůru od aktuální vrstvy a zastaví se na první, která
 * není ve schvalovatelném stavu. Ve scope **termínu** navíc respektuje strop – vrstvy, které seriál ještě
 * nemá schválené, do řetězu nepustí. Ve scope **seriálu** (bez `serieLayers`) strop není. Nabízet vrstvy za
 * touto hranicí by znamenalo nabízet akci, která na backendu selže.
 */
export function buildApproveLayerPlan({
  layers,
  serieLayers,
  currentLayer,
}: BuildApproveLayerPlanInput): ApproveLayerPlan {
  const ordered = [...layers].sort((a, b) => a.sortOrder - b.sortOrder);
  const current = ordered.find((entry) => entry.layer === currentLayer);

  // Schvalovat lze jen první neschválenou vrstvu (schvalovatelný stav + předchozí vrstva Approved).
  if (
    !current ||
    !isApprovable(current.layer, current.state) ||
    !isPreviousApproved(ordered, current)
  ) {
    return { primary: null, through: [] };
  }

  const primary = toOption(current);

  // Strop řetězu: ve scope termínu jen vrstvy schválené na seriálu; ve scope seriálu bez stropu.
  const serieStateBySortOrder = serieLayers
    ? new Map(serieLayers.map((entry) => [entry.sortOrder, entry.state]))
    : null;

  const through: ApproveLayerOption[] = [];

  for (const entry of ordered) {
    if (entry.sortOrder <= current.sortOrder) continue;

    // Termín: seriál vrstvu ještě nemá schválenou → dál řetěz nepustíme (strop). Seriál strop nemá.
    if (serieStateBySortOrder) {
      const serieState = serieStateBySortOrder.get(entry.sortOrder);
      if (serieState === undefined || !isApproved(serieState)) break;
    }

    // Vrstva už je hotová – řetěz nepřerušuje, jen se nenabízí jako cíl.
    if (isApproved(entry.state)) continue;

    // Vrstva na cestě není ve schvalovatelném stavu → řetěz by tam selhal.
    if (!isApprovable(entry.layer, entry.state)) break;

    through.push(toOption(entry));
  }

  return { primary, through };
}
